import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Skeleton,
} from "@mui/material";

import { Invoice } from "../../../payment.types";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { usePaymentHistory } from "../../../../../api/queries/payment/paymentQueries";

import NoPaidUsersFigure from "../NoPaidUsersFigure";

import TableHeading from "./TableHeading";
import TableControl from "./TableControl";
import { InvoicePopUp } from "./InvoicePopUp";
import PaymentTableLoading from "./PaymentTableLoading";
import { StatusCell } from "./TableCustomCells/StatusCell";
import { BodyCellStyle, HeadCellStyle } from "./tableStyles";
import { CategoryCell } from "./TableCustomCells/CategoryCell";
import { InvoiceMenuCell } from "./TableCustomCells/InvoiceMenuCell";

const PaymentTable = () => {
  const { t } = useTranslation();
  const [activeState, setActiveState] = useState<"success" | "failed" | undefined>();
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 1000);
  const [openInvoicePopUp, setOpenInvoicePopUp] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState<null | Invoice>(null);
  const [page, setPage] = useState<number>(0);
  const [invoiceId, setInvoiceId] = useState<number | null>(null);

  const handleChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setSearchValue(e.target.value);

  const handleOnDownloadInvoice = (invoiceId: number) => {
    setOpenInvoicePopUp(true);
    setInvoiceId(invoiceId);
  };

  const handleOnCloseDownloadInvoicePopUp = () => {
    setOpenInvoicePopUp(false);
    setInvoiceId(null);
  };

  const { userPaymentHistory, isUserPaymentHistoryLoading, isUserPaymentHistoryHasError } = usePaymentHistory({
    page,
    search: debouncedSearchValue,
    status: activeState,
  });

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => setPage(value - 1);

  if (isUserPaymentHistoryLoading) return <PaymentTableLoading />;

  const NO_PAYMENTS =
    (!isUserPaymentHistoryLoading && userPaymentHistory?.items.length === 0) || isUserPaymentHistoryHasError;

  return (
    <Box
      sx={{
        boxShadow: "none",
        borderRadius: "12px important",
        border: "1px solid #EAECF0",
        background: "#FFF",
        display: "flex",
        flexDirection: "column",
        rowGap: "16px",
      }}
    >
      <TableHeading />
      <TableControl
        activeState={activeState}
        setActiveState={setActiveState}
        value={searchValue}
        handleOnValueChange={handleChangeText}
      />
      {NO_PAYMENTS && <NoPaidUsersFigure />}
      {!NO_PAYMENTS && (
        <TableContainer component={Paper}>
          <Table aria-label="payments table">
            <TableHead>
              <TableRow>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.amount")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.invoiceNum")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.status")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.paidBy")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.email")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.category")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.paidAt")}</TableCell>
                <TableCell sx={HeadCellStyle}>{t("pages.payment.paymentLinks.created")}</TableCell>
                <TableCell
                  sx={{
                    borderBottom: "1px solid var(--Gray-200, #EAECF0)",
                    background: "var(--Gray-50, #F9FAFB)",
                    padding: "12px 16px",
                  }}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {userPaymentHistory &&
                userPaymentHistory.items.map((payment, index) => (
                  <TableRow key={index}>
                    <TableCell sx={BodyCellStyle}>{payment.amount}</TableCell>
                    <TableCell sx={BodyCellStyle}>{payment.invoiceNum}</TableCell>
                    <StatusCell status={payment.status} />
                    <TableCell sx={BodyCellStyle}>{payment.paidBy}</TableCell>
                    <TableCell sx={BodyCellStyle}>{payment.email}</TableCell>
                    <CategoryCell category={payment.category} />
                    <TableCell sx={BodyCellStyle}>{format(new Date(payment.paidAt), "dd/MM/yyyy")}</TableCell>
                    <TableCell sx={BodyCellStyle}>{format(new Date(payment.created), "dd/MM/yyyy")}</TableCell>
                    <InvoiceMenuCell handleOnDownloadInvoice={() => handleOnDownloadInvoice(payment.id)} />
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!NO_PAYMENTS && userPaymentHistory && userPaymentHistory?.items?.length > 0 && (
        <Pagination
          count={userPaymentHistory?.meta?.totalPages}
          page={!userPaymentHistory?.meta?.totalItems || userPaymentHistory?.meta?.totalItems <= 0 ? 1 : page + 1}
          onChange={handleChangePage}
          sx={{
            margin: "0 auto 20px",
            direction: "ltr !important",
            "& .MuiPagination-ul": {
              justifyContent: "center",
            },
            "& .MuiButtonBase-root": {
              borderRadius: "8px !important",
            },
          }}
        />
      )}

      <InvoicePopUp onClose={handleOnCloseDownloadInvoicePopUp} open={openInvoicePopUp} invoiceId={invoiceId} />
    </Box>
  );
};

export default PaymentTable;
